import React, { useMemo } from 'react'
import { Modal, Stepper } from '@enamelai/design-system'
import './PortFaxStyle.css'
import { ITwilioPortRequest } from '@dataTypes/clinic.type'
import PortRequestDeclineBanner from '../../shared/PortRequestDeclineBanner.component'
import { getPortRequestDeclineInfo } from '@utils/portRequestPayload.util'
import { EnterDetails, ReviewInfo, SelectNumber } from './Steps'
import usePortFax from './usePortFax'

type RunAsyncFn = <T>(
  operation: () => Promise<T>,
  messages: {
    successTitle?: string
    successMessage?: string
    errorTitle: string
    errorFallbackMessage: string
  },
) => Promise<T | undefined>

type Props = {
  show: boolean
  clinicId: string
  onHide: () => void
  onAdded: () => void
  resubmitRequest?: ITwilioPortRequest | null
  runAsync?: RunAsyncFn
  loadingContainer?: string
}

const PortFaxModal = ({
  show,
  clinicId,
  onHide,
  onAdded,
  resubmitRequest = null,
  runAsync,
  loadingContainer,
}: Props): React.ReactElement => {
  const hooks = usePortFax({
    show,
    clinicId,
    onHide,
    onAdded,
    resubmitRequest,
    runAsync,
    loadingContainer,
  })

  const declineInfo = useMemo(
    () =>
      hooks.resubmitRequest
        ? getPortRequestDeclineInfo(hooks.resubmitRequest)
        : null,
    [hooks.resubmitRequest],
  )

  const getSectionComponent = (): React.ReactElement => {
    switch (hooks.step) {
      case 0:
        return (
          <SelectNumber
            numberData={hooks.numberData}
            handleChange={hooks.handleNumberChange}
            nonPortableNumberHint={null}
            validationErrors={hooks.validationErrors}
          />
        )
      case 1:
        return (
          <EnterDetails
            data={hooks.detailsData}
            handleChange={hooks.handleDetailsChange}
            validationErrors={hooks.validationErrors}
          />
        )
      case 2:
        return (
          <ReviewInfo
            numberData={hooks.numberData}
            detailsStep2Data={hooks.detailsData}
          />
        )
      default:
        return <></>
    }
  }

  const isLastStep = hooks.step === hooks.steps.length - 1

  return (
    <Modal
      open={show}
      onClose={hooks.handleClose}
      closeOnBackdrop={false}
      className="scp-fax-port-modal"
      size="md"
      title={
        hooks.isResubmitMode ? 'Resubmit fax port request' : 'Port fax number'
      }
      primaryLabel={
        isLastStep ? (hooks.isResubmitMode ? 'Resubmit' : 'Proceed') : 'Next'
      }
      secondaryLabel={hooks.step === 0 ? 'Cancel' : 'Previous'}
      primaryDisabled={hooks.busy}
      onPrimaryClick={() => {
        if (hooks.busy) return
        if (isLastStep) {
          void hooks.handleProceedSave()
          return
        }
        void hooks.handleNext()
      }}
      onSecondaryClick={() => {
        if (hooks.busy) return
        if (hooks.step === 0) {
          hooks.handleClose()
          return
        }
        hooks.handlePrevious()
      }}
    >
      <Stepper steps={hooks.steps} currentStep={hooks.step} />
      {hooks.isResubmitMode && declineInfo ? (
        <PortRequestDeclineBanner declineInfo={declineInfo} />
      ) : null}
      <div className="scp-fax-step" key={hooks.step}>
        {getSectionComponent()}
      </div>
    </Modal>
  )
}

export default PortFaxModal
